import React, { useState } from 'react';
import { Box, Heading } from 'lmc-ui-library';
import {
  ButtonWrapper,
  HeadingWrapper,
  UpdatedInformation,
} from 'screens/Panel/components/DueDiligence/DueDiligancePage.styled';
import { ApproveButton, DeclineButton } from 'screens/Panel/components/DueDiligence/Button';
import { ApprovalLine, DueDiligenceStatus, LineApprovalStatus } from 'screens/Panel/types/dueDiligence';
import { DueDiligence } from 'utils/api/types';
import { Nullable } from 'utils/types';
import ErrorModal from 'screens/Panel/components/ErrorModal/ErrorModal';
import { format } from 'utils/date';

interface Props {
  onUpdateStatus: (approvalLine: ApprovalLine, status: DueDiligenceStatus) => Promise<any>;
  dueDiligence: DueDiligence;
  hasFirstLinePermissions: boolean;
  hasSecondLinePermissions: boolean;
  disableApprove?: boolean;
  beforeContent?: any;
}

const ApproveBox: React.FC<Props> = ({
  dueDiligence,
  onUpdateStatus,
  hasFirstLinePermissions,
  hasSecondLinePermissions,
  disableApprove = false,
  beforeContent,
}) => {
  const [modalError, setModalError] = useState<Nullable<Error>>(null);
  const [approvalLines, setApprovalLines] = useState<{
    [ApprovalLine.FIRST]: LineApprovalStatus;
    [ApprovalLine.SECOND]: LineApprovalStatus;
  }>({
    [ApprovalLine.FIRST]: {
      status: dueDiligence.first_line_status as LineApprovalStatus['status'],
      isLoading: false,
    },
    [ApprovalLine.SECOND]: {
      status: dueDiligence.second_line_status as LineApprovalStatus['status'],
      isLoading: false,
    },
  });

  const handleUpdateStatus = async (approvalLine: ApprovalLine, status: DueDiligenceStatus) => {
    try {
      setApprovalLines({
        ...approvalLines,
        [approvalLine]: {
          ...approvalLines[approvalLine],
          isLoading: true,
        },
      });
      await onUpdateStatus(approvalLine, status);
    } catch (e) {
      setApprovalLines({
        ...approvalLines,
        [approvalLine]: {
          ...approvalLines[approvalLine],
          isLoading: false,
        },
      });
      setModalError(e);
    }
  };

  return (
    <>
      <Box>
        <Box.Content>
          {beforeContent}
          <div>
            <HeadingWrapper>
              <Heading mode="subheading" variant="secondary" weight="bold">
                1st line approval
              </Heading>
            </HeadingWrapper>
            <ButtonWrapper>
              <ApproveButton
                isLoading={approvalLines[ApprovalLine.FIRST].isLoading}
                isApproved={approvalLines[ApprovalLine.FIRST].status === DueDiligenceStatus.APPROVED}
                onClick={() => handleUpdateStatus(ApprovalLine.FIRST, DueDiligenceStatus.APPROVED)}
                isDisabled={!hasFirstLinePermissions || disableApprove}
              />
              <DeclineButton
                isLoading={approvalLines[ApprovalLine.FIRST].isLoading}
                isDeclined={approvalLines[ApprovalLine.FIRST].status === DueDiligenceStatus.DECLINED}
                onClick={() => handleUpdateStatus(ApprovalLine.FIRST, DueDiligenceStatus.DECLINED)}
                isDisabled={!hasFirstLinePermissions}
              />
            </ButtonWrapper>
            {dueDiligence.first_line_updated_by && (
              <UpdatedInformation>
                Updated by: <b>{dueDiligence.first_line_updated_by}</b>
              </UpdatedInformation>
            )}
            {dueDiligence.first_line_updated_at && (
              <UpdatedInformation>
                Date: {format(dueDiligence.first_line_updated_at).fromBackendToView()}
              </UpdatedInformation>
            )}
          </div>
          <div style={{ marginTop: '60px' }}>
            <HeadingWrapper>
              <Heading mode="subheading" variant="secondary" weight="bold">
                2nd line approval
              </Heading>
            </HeadingWrapper>
            <ButtonWrapper>
              <ApproveButton
                isLoading={approvalLines[ApprovalLine.SECOND].isLoading}
                isApproved={approvalLines[ApprovalLine.SECOND].status === DueDiligenceStatus.APPROVED}
                onClick={() => handleUpdateStatus(ApprovalLine.SECOND, DueDiligenceStatus.APPROVED)}
                isDisabled={!hasSecondLinePermissions || !dueDiligence.first_line_status || disableApprove}
              />
              <DeclineButton
                isLoading={approvalLines[ApprovalLine.SECOND].isLoading}
                isDeclined={approvalLines[ApprovalLine.SECOND].status === DueDiligenceStatus.DECLINED}
                onClick={() => handleUpdateStatus(ApprovalLine.SECOND, DueDiligenceStatus.DECLINED)}
                isDisabled={!hasSecondLinePermissions || !dueDiligence.first_line_status}
              />
              {dueDiligence.second_line_updated_by && (
                <UpdatedInformation>
                  Updated by: <b>{dueDiligence.second_line_updated_by}</b>
                </UpdatedInformation>
              )}
              {dueDiligence.second_line_updated_at && (
                <UpdatedInformation>
                  Date: {format(dueDiligence.second_line_updated_at).fromBackendToView()}
                </UpdatedInformation>
              )}
            </ButtonWrapper>
          </div>
        </Box.Content>
      </Box>
      <ErrorModal show={modalError !== null} error={modalError} onClose={() => setModalError(null)} />
    </>
  );
};

export default ApproveBox;
